import { Box, Chip, Divider, Paper, Typography, useMediaQuery } from '@mui/material';

import { enPesos, enPorcentaje, precioParaMostrar, tieneDescuentoPorBulto } from '@/lib/catalogo';
import type { Producto } from '@/tipos';

import { CodigoDePieza } from '@/components/CodigoDePieza';

interface Props {
  producto: Producto;
  /** Sin sesión no se muestran ni precios ni condiciones comerciales. */
  conPrecios: boolean;
}

function Dato({
  etiqueta,
  valor,
  destacado = false,
}: {
  etiqueta: string;
  valor: string;
  destacado?: boolean;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 90 }}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          textTransform: 'uppercase',
          fontSize: '0.7rem',
          letterSpacing: 0.5,
        }}
      >
        {etiqueta}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: destacado ? 'success.main' : 'text.primary',
          whiteSpace: 'nowrap',
        }}
      >
        {valor}
      </Typography>
    </Box>
  );
}

export function FichaDelProducto({ producto, conPrecios }: Props) {
  const angosto = useMediaQuery('(max-width:600px)');

  const { descuento, conOferta } = precioParaMostrar(producto);

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        p: 2,
        bgcolor: '#1e1e1e',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/*
        La cabecera pasó de un renglón apretado -chip, código, barrita, descripción, stock, todo
        en la misma línea- a tres alturas: el rubro como antetítulo, el código como título de la
        página y la descripción abajo.

        El código es el título de verdad: es lo que el cliente dice por teléfono y lo que escribe
        para buscar. Además la ficha no tenía ningún h1; el único del archivo estaba en la
        pantalla de "producto no encontrado".
      */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Chip label={producto.category} color="primary" size="small" />

        {conPrecios && (
          <Chip
            label={producto.is_in_stock ? 'Stock Disponible' : 'Sin Stock'}
            color={producto.is_in_stock ? 'success' : 'error'}
            size="small"
            sx={{ ml: angosto ? 0 : 'auto' }}
          />
        )}
      </Box>

      <Box
        component="h1"
        // El h1 envuelve código y descripción para que el título de la página tenga texto
        // buscable y no sólo una sigla. Sin tamaño ni peso propios: los ponen los hijos, y si no
        // el navegador le mete su 2em en negrita.
        sx={{
          m: 0,
          fontSize: 'inherit',
          fontWeight: 400,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {/* Un guion que no se ve, entre el código y la descripción. En pantalla son dos
            renglones y se leen separados; en el texto del h1 quedaban pegados —
            "ACS001CARGADOR USB PARA MOTO"— y así lo leía un buscador. */}
        <span
          // Con `style` y no con `sx`: un `sx` crea una clase y su bloque de CSS, y ese bloque
          // viaja adentro del h1. Para tres caracteres que no se ven, no vale la pena.
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clipPath: 'inset(50%)',
          }}
        >
          {' — '}
        </span>

        <CodigoDePieza
          codigo={producto.code}
          variante="display"
          // El mismo nombre que lleva el código en la tarjeta del catálogo: al abrir la ficha,
          // uno se convierte en el otro en vez de aparecer de la nada.
          sx={{ viewTransitionName: 'codigo-producto' }}
        />

        <Box sx={{ width: 48, height: '3px', bgcolor: 'error.main' }} />

        <Typography
          component="span"
          variant="body1"
          sx={{ maxWidth: '62ch', color: 'text.primary' }}
        >
          {producto.description}
        </Typography>
      </Box>

      {conPrecios && (
        <>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

          <Box
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, rowGap: 2, alignItems: 'flex-start' }}
          >
            {producto.price !== null && (
              <Dato etiqueta="Precio Lista" valor={enPesos(producto.price)} destacado />
            )}

            {producto.bulk?.trim() && <Dato etiqueta="Bulto" valor={producto.bulk} />}
            {producto.cut?.trim() && <Dato etiqueta="Corte" valor={producto.cut} />}

            {descuento !== null && (
              <Dato etiqueta="Porcentaje Descuento" valor={`${enPorcentaje(descuento)}%`} />
            )}

            {conOferta !== null && descuento !== null && (
              <Dato etiqueta="Precio Oferta" valor={enPesos(conOferta)} destacado />
            )}

            <Dato
              etiqueta="Descuento por Bulto"
              valor={tieneDescuentoPorBulto(producto.bulk_discount) ? 'Sí' : 'No'}
            />

            {producto.estado_raw?.trim() && <Dato etiqueta="Estado" valor={producto.estado_raw} />}
          </Box>
        </>
      )}
    </Paper>
  );
}
